import {Stack, Text} from '@mantine/core';
import React, {useCallback} from 'react';

import {parseSDF} from 'bulk-import/SpreadsheetBulkImportDAO';
import SpreadsheetUploadPane from 'spreadsheet-import-wizard/shared/SpreadsheetUploadPane';
import type {SpreadsheetUploadResult} from 'spreadsheet-import-wizard/shared/SpreadsheetUploadPane';
import * as ImportUtil from 'util/ImportUtil';

import type {ModalAction, ModalState} from './moleculeImportReducer';
import SpreadsheetExampleTable from './SpreadsheetExampleTable';
import useMoleculeSchemasPairing from './useMoleculeSchemasPairing';

const SDF_EXTENSIONS = ['.sdf', '.sd'];

function isSdfFile(file: File): boolean {
  const lower = file.name.toLowerCase();
  return SDF_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

interface FileUploadPaneProps {
  state: ModalState;
  dispatch: React.Dispatch<ModalAction>;
}

export default function FileUploadPane({state, dispatch}: FileUploadPaneProps): JSX.Element {
  const {pairingMap} = useMoleculeSchemasPairing(null);
  const pairing = state.schemaId ? pairingMap[state.schemaId] : undefined;

  const handleSdfUpload = useCallback(
    async (file: File): Promise<{s3Url: string; headers: Array<string>}> => {
      const s3Url = (await ImportUtil.upload(file))!;
      const result = (await parseSDF(file.name, s3Url))!;
      return {s3Url: result.updatedUrl, headers: result.columnNames};
    },
    []
  );

  const handleUploadComplete = useCallback(
    (result: SpreadsheetUploadResult) => {
      dispatch({
        type: 'SET_FILE_UPLOAD_RESULT',
        payload: {file: result.file, s3Url: result.s3Url, headers: result.headers},
      });
    },
    [dispatch]
  );

  const handleRemove = useCallback(() => {
    dispatch({type: 'CLEAR_FILE_UPLOAD'});
  }, [dispatch]);

  return (
    <Stack spacing="md">
      <div>
        <Text weight={600} size="md">
          Upload file
        </Text>
        <Text size="sm" color="dimmed">
          Upload a CSV, Excel, or SDF file to import into Benchling (example below). The first row should
          specify column headers, which must be unique. Each row after that should specify an individual
          molecule.
        </Text>
      </div>

      <SpreadsheetExampleTable schemaId={state.schemaId} pairing={pairing} />

      <SpreadsheetUploadPane
        accept=".csv,.xls,.xlsx,.sdf,.sd"
        title="Upload a CSV, Excel, or SDF file"
        customFileHandler={{shouldHandle: isSdfFile, handle: handleSdfUpload}}
        onUploadComplete={handleUploadComplete}
        onRemove={handleRemove}
        localeDescription="Tell us how numbers and dates are formatted in your file."
      />
    </Stack>
  );
}
